import { useCallback, useState } from "react"
import { IconTriangleWarningOutline18 as WarningIcon } from "nucleo-ui-outline-18"

import { getSlackDeliveryState } from "@productlane/api/src/browser"
import { Badge, Button } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { useTimeTick } from "@/lib/hooks/use-time-tick"
import { mutateOk, useZero } from "@/lib/zero"

interface Props {
	message: {
		id: string
		type: string
		slackId: string
		createdAt: number
	}
}

export function SlackDeliveryNotice({ message }: Props) {
	useTimeTick(15_000)
	const zero = useZero()
	const [isRetrying, setIsRetrying] = useState(false)

	const state = getSlackDeliveryState({
		type: message.type === "OUTBOUND" ? "OUTBOUND" : "INBOUND",
		slackId: message.slackId,
		createdAt: message.createdAt,
		now: Date.now(),
	})

	const retry = useCallback(async () => {
		setIsRetrying(true)
		try {
			await mutateOk(
				zero.mutate(mutators.slackMessages.resend({ id: message.id })),
				"server",
			)
		} catch (error) {
			toast.error({
				title: "Slack reply not sent",
				description:
					error instanceof Error
						? error.message
						: "Nothing reached Slack. Try again.",
			})
		} finally {
			setIsRetrying(false)
		}
	}, [message.id, zero])

	if (state === "delivered" || state === "sending") return null

	return (
		<div className="flex items-center gap-2">
			<Badge variant="red" size="sm">
				<WarningIcon className="mr-1 h-3 w-3 flex-none" />
				Not delivered
			</Badge>
			<Button
				size="sm"
				variant="link"
				className="h-auto min-h-0 px-0 text-xs font-normal text-tertiary hover:text-primary"
				isLoading={isRetrying}
				onClick={retry}
			>
				Send again
			</Button>
		</div>
	)
}
